import { UnitDef } from './api/types.js';
import { listUnits } from './api/units.js';

/**
 * Singleton cache for unit definitions.
 * Fetches all units from the API once and caches them.
 * Call `invalidate()` after any CRUD operation to force a refresh.
 */
class UnitCache {
  private units: Map<string, UnitDef> = new Map();
  private loaded = false;
  private loadPromise: Promise<void> | null = null;

  async ensureLoaded(): Promise<void> {
    if (this.loaded) return;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = this.fetchUnits().then(() => {
      this.loaded = true;
      this.loadPromise = null;
    }).catch(err => {
      this.loadPromise = null;
      throw err;
    });

    return this.loadPromise;
  }

  private async fetchUnits(): Promise<void> {
    const units = await listUnits();
    this.units.clear();
    for (const u of units) {
      this.units.set(u.id, u);
    }
  }

  get(id: string): UnitDef | undefined {
    return this.units.get(id);
  }

  getAll(): UnitDef[] {
    return Array.from(this.units.values());
  }

  /**
   * Get the abbreviation for a unit by its ID.
   * Falls back to the ID itself if the unit is not found.
   */
  getAbbreviation(id: string): string {
    const def = this.units.get(id);
    if (!def) return id;
    if (def.abbreviation) return def.abbreviation;
    return def.plural;
  }

  /**
   * Get the display name for a unit, choosing singular or plural
   * based on the amount.
   *
   * - If amount is exactly 1, use singular
   * - Otherwise use plural
   */
  getDisplayName(id: string, amount?: number): string {
    const def = this.units.get(id);
    if (!def) return id;
    if (amount != null && amount === 1) return def.singular;
    return def.plural;
  }

  /**
   * Get the plural name for a unit by its ID.
   * Falls back to the ID itself if the unit is not found.
   */
  getPlural(id: string): string {
    const def = this.units.get(id);
    return def?.plural ?? id;
  }

  /**
   * Get the singular name for a unit by its ID.
   * Falls back to the ID itself if the unit is not found.
   */
  getSingular(id: string): string {
    const def = this.units.get(id);
    return def?.singular ?? id;
  }

  /**
   * Mark the cache as stale so it will be re-fetched on next access.
   */
  invalidate(): void {
    this.loaded = false;
  }
}

export const unitCache = new UnitCache();
